import type { Strings } from '../index.ts';
import { unreadable } from './shared.ts';

/**
 * Die Kanalseite.
 *
 * `tab` sind die Namen auf dem Reiterstreifen, `tabNoun` dieselben vier als **Substantive in einem Satz**
 * — im Deutschen groß, anders als im Englischen, wo die Satzform kleingeschrieben ist. Beide Listen
 * bleiben getrennt: der Streifen sagt „Live“, weil das der Name eines Tabs in einem Wort ist, und
 * „Auf diesem Kanal gibt es keine Live“ ist kein Satz.
 *
 * `jumpOut.label` heißt „Anzeigen auf“ und nicht „Ansehen auf“, weil ein Kanal angesehen wird und kein
 * Video ist. Die Watch-Seite behält „Ansehen auf“, damit die beiden Wege nach draußen sich unterscheiden.
 */
export const channel: Strings['channel'] = {
  tab: {
    videos: 'Videos',
    shorts: 'Shorts',
    streams: 'Live',
    playlists: 'Playlists',
    about: 'Info',
  },

  tabNoun: {
    videos: 'Videos',
    shorts: 'Shorts',
    streams: 'Livestreams',
    playlists: 'Playlists',
  },

  badge: {
    autoGenerated: 'Automatisch von YouTube erstellt',
    ageGated: 'Altersbeschränkt',
  },

  sort: {
    label: 'Sortierung',
    newest: 'Neueste',
    oldest: 'Älteste',
    popular: 'Meistgesehen',
  },

  notice: {
    streamsLiveUnreportedOne:
      'Einer dieser Streams läuft möglicherweise gerade live. Diese Liste lässt das nicht erkennen, deshalb wird kein LIVE-Hinweis darauf gezeichnet. Öffne ihn, um es zu sehen.',
    streamsLiveUnreported: (count) =>
      `${count} dieser Streams laufen möglicherweise gerade live. Diese Liste lässt das nicht erkennen, deshalb wird kein LIVE-Hinweis darauf gezeichnet. Öffne einen, um es zu sehen.`,
  },

  allDropped: {
    title: 'Dieser Tab konnte nicht gelesen werden',
    detail: 'Die Instanz hat geantwortet, und nichts darin ließ sich anzeigen.',
  },

  empty: {
    videos: {
      title: 'Keine Videos',
      detail: 'Auf diesem Kanal gibt es keine Videos.',
    },
    shorts: {
      title: 'Keine Shorts',
      detail: 'Dieser Kanal hat keine veröffentlicht, oder YouTube hat den Tab nicht geliefert.',
    },
    streams: {
      title: 'Keine Streams',
      detail:
        'Nichts live und keine vergangenen Übertragungen. Beendete Streams bleiben hier normalerweise gelistet.',
    },
    playlists: {
      title: 'Keine öffentlichen Playlists',
      detail: 'Private Playlists sieht niemand außer ihrem Besitzer, angemeldet oder nicht.',
    },
    undeclaredTitle: (tabLabel) => `Dieser Kanal hat keinen Tab „${tabLabel}“`,
    undeclaredDetail: (tabNoun) => `Auf diesem Kanal gibt es keine ${tabNoun}.`,
  },

  page: {
    verified: 'Verifiziert',
    subscribers: (count) => `${count} Abonnenten`,
    tabsLabel: 'Kanalbereiche',
    loadingTab: (tab) => `${tab} auf diesem Kanal werden geladen…`,
    gridLabel: (tab) => `${tab} auf diesem Kanal`,
    loadMore: (soFar) => `Mehr laden (bisher ${soFar})`,
    loadingMore: 'Wird geladen…',
    loadingAbout: 'Beschreibung dieses Kanals wird geladen…',
    noDescription: 'Dieser Kanal hat keine Beschreibung.',
    totalViewsLabel: 'Aufrufe insgesamt',
    keywords: 'Schlagwörter',
    jumpOut: {
      label: 'Anzeigen auf',
      targets: {
        youtube: 'YouTube',
        instance: 'Invidious',
      },
    },
  },

  search: {
    label: 'Diesen Kanal durchsuchen',
    placeholder: 'Diesen Kanal durchsuchen',
    clear: 'Suche löschen',
    activeTitle: (q) => `Dieser Kanal wird nach „${q}“ durchsucht`,
    submit: 'Suchen',
    resultsFor: (q) => `Ergebnisse für „${q}“`,
    gridLabel: 'Suchergebnisse auf diesem Kanal',
    loading: 'Dieser Kanal wird durchsucht…',
    emptyTitle: 'Nichts gefunden',
    emptyDetail: (q) =>
      `Auf diesem Kanal passt nichts zu „${q}“. YouTube beantwortet diese Suche, ein neuer Upload kann das also ändern.`,
  },

  problem: {
    notFound: {
      title: 'Kein solcher Kanal',
      detail:
        'Die Instanz sieht keinen Kanal mit dieser ID. Entweder hat er nie existiert, oder er wurde gelöscht oder gesperrt.',
      retryable: false,
    },
    unreadable: unreadable('Dieser Tab konnte nicht gelesen werden'),
    upstreamBroken: {
      title: 'YouTube liefert diesen Kanal nicht',
      detail:
        'Deine Instanz hat gefragt und einen Fehler zurückbekommen statt einer Seite. Automatisch erstellte „- Topic“-Kanäle antworten jedes Mal so, und ein paar Kanäle mit ungewöhnlichem Aufbau ebenfalls. Ein neuer Versuch bekommt dieselbe Antwort.',
      retryable: false,
    },
  },
};
